import FeatureAttachments from "components/feature-attachments";
import {
  EntityType,
  ESubmittalStatus,
  EUserTypes,
  ProjectPermissionEnum
} from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext,
  TProjectContext
} from "context/ProjectProvider";
import { TFetureDetailUpdateAction } from "entity-app/models";
import EditableDetailsCardWrapper from "entity-app/shared-components/editable-details-card-wrapper/editable-details-card-wrapper";
import { useContext, useMemo } from "react";
import {
  useIntegrationProjectParticipants,
  useProjectParticipants
} from "hooks/project-participants";
import { isProjectInIntegrationMode, matchUserProjectRole } from "utils/utils";
import { ErrorBoundary } from "@sentry/react";
import { Link, useParams } from "react-router-dom";
import { FeatureTypes } from "entity-app/constants";
import SubmittalWorkflowResponse from "./components/submittal-workflow-response";
import SubmittalLinkedTopSection from "./components/submittal-linked-top-section";
import SubmittalGeneralInfo from "./components/submittal-general-info";
import SubmittalWorkflow from "./components/submittal-workflow";
import { SubmittalDetailLinks } from "./components/submittal-details-links";
import FeatureHistory from "../../../components/feature-history/feature-history";

function SubmittalDetailsCards(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  featureId: string;
  setTabIndex: (tab: string) => void;
  setAddMore: any;
  workflowOverride: {
    isWorkflowInOverrideMode: boolean;
    setIsWorkflowInOverrideMode: Function;
  };
  isRevisionMode: boolean;
  submittal: any;
  editableFields: any[];
  updateFeatureData: {
    localFeatureInstanceData: any;
    setLocalFeatureInstanceData: Function;
  };
  submittalViewState: any;
}) {
  const {
    featureInstanceData,
    actions,
    featureId,
    setTabIndex,
    setAddMore,
    workflowOverride: { isWorkflowInOverrideMode, setIsWorkflowInOverrideMode },
    isRevisionMode,
    submittal,
    editableFields,
    updateFeatureData: {
      localFeatureInstanceData,
      setLocalFeatureInstanceData
    },
    submittalViewState
  } = props;
  const {
    tokenContents,
    projectDetails,
    tokenRetrievalState,
    gqlClientForProject
  }: TProjectContext = useContext(ProjectContext);
  const { projectId } = useParams() as any;

  const isLoggedInUserGC = matchUserProjectRole(
    EUserTypes.GENERAL_CONTRACTOR,
    tokenContents?.role
  );

  const isIntegrationMode = isProjectInIntegrationMode(
    projectDetails ? projectDetails.mode : 0
  );

  const { projectParticipants: WFProjectParticipants } = useProjectParticipants(
    isIntegrationMode || !isLoggedInUserGC
  );
  const { data: INTGProjectParticipants } = useIntegrationProjectParticipants(
    !isIntegrationMode || !isLoggedInUserGC
  );

  const projectParticipants = isIntegrationMode
    ? INTGProjectParticipants
    : WFProjectParticipants;

  const canEditLinkingPage = !isPermissionNotGrantted(
    ProjectPermissionEnum.EditLinkingPage,
    tokenContents?.role!
  );

  const isVoidSubmittal: boolean = useMemo(() => {
    return submittal?.workflow_status === ESubmittalStatus.VOID;
  }, [submittal?.workflow_status]);

  const isDeletedSubmittal: boolean = useMemo(() => {
    return submittal?.deleted_from_source;
  }, [submittal?.deleted_from_source]);

  return (
    <div
      className="flex flex-col p-4 space-y-4"
      data-testid="submittal-cards-root"
    >
      <SubmittalLinkedTopSection
        actions={actions}
        featureInstanceData={localFeatureInstanceData}
        isRevisionMode={isRevisionMode}
        data-testid="submittal-cards-linked-top-section"
      />
      <div className="grid grid-cols-3 gap-4 pb-10">
        <div className="flex flex-col gap-4">
          <EditableDetailsCardWrapper
            title="General Information"
            data-testid="submittal-cards-general-info-card"
          >
            <ErrorBoundary>
              <SubmittalGeneralInfo
                featureInstanceData={featureInstanceData}
                actions={actions}
                submittal={submittal}
                editableFields={editableFields}
                isRevisionMode={isRevisionMode}
                updateFeatureData={{
                  localFeatureInstanceData,
                  setLocalFeatureInstanceData
                }}
                data-testid="submittal-cards-general-info"
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
          {!isIntegrationMode && (
            <EditableDetailsCardWrapper
              title="Attachments"
              data-testid="submittal-cards-attachments-card"
            >
              <ErrorBoundary>
                <div
                  className="h-[320px] overflow-auto"
                  data-testid="submittal-cards-attachments-container"
                >
                  <FeatureAttachments
                    featureId={featureId}
                    featureKey="feature_instance_id"
                    setTabIndex={setTabIndex}
                    disabled={
                      !isWorkflowInOverrideMode &&
                      (isRevisionMode ||
                        isPermissionNotGrantted(
                          ProjectPermissionEnum.AddAttachment,
                          tokenContents?.role!
                        ) ||
                        !editableFields.includes("attachment"))
                    }
                    featureType={EntityType.Submittal}
                    setAddMore={setAddMore}
                    isRevisionMode={isRevisionMode}
                    featureInstanceData={localFeatureInstanceData}
                    data-testid="submittal-cards-attachments"
                  />
                </div>
              </ErrorBoundary>
            </EditableDetailsCardWrapper>
          )}
        </div>
        <div className="flex flex-col gap-4 max-h-full">
          <EditableDetailsCardWrapper
            title="Workflow"
            data-testid="submittal-cards-workflow-card"
          >
            <ErrorBoundary>
              <SubmittalWorkflow
                featureInstanceData={featureInstanceData}
                isRevisionMode={isRevisionMode}
                actions={actions}
                projectParticipants={projectParticipants}
                submittal={submittal}
                editableFields={editableFields}
                updateFeatureData={{
                  localFeatureInstanceData,
                  setLocalFeatureInstanceData
                }}
                submittalViewState={submittalViewState}
                data-testid="submittal-cards-workflow"
              />
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
          {!isIntegrationMode && (
            <EditableDetailsCardWrapper
              title="Workflow Response"
              extra={
                isWorkflowInOverrideMode && (
                  <span className="text-sm text-amber-600">
                    You are overriding the workflow
                  </span>
                )
              }
              data-testid="submittal-cards-workflow-response-card"
            >
              <ErrorBoundary>
                <div
                  className=""
                  data-testid="submittal-cards-workflow-response-container"
                >
                  <SubmittalWorkflowResponse
                    actions={actions}
                    projectId={projectId}
                    submittal={submittal}
                    gqlClientForProject={gqlClientForProject}
                    isRevisionMode={isRevisionMode}
                    editableFields={editableFields}
                    workflowOverride={{
                      isWorkflowInOverrideMode,
                      setIsWorkflowInOverrideMode
                    }}
                    tokenRetrievalState={tokenRetrievalState}
                    submittalViewState={submittalViewState}
                    updateFeatureData={{
                      localFeatureInstanceData,
                      setLocalFeatureInstanceData
                    }}
                    data-testid="submittal-cards-workflow-response"
                  />
                </div>
              </ErrorBoundary>
            </EditableDetailsCardWrapper>
          )}
        </div>
        <div className="flex flex-col gap-4">
          <EditableDetailsCardWrapper
            title="Links"
            extra={
              canEditLinkingPage && !isVoidSubmittal && !isDeletedSubmittal ? (
                <Link
                  to={`/project/${projectId}/schedule/submittal-linking?&SubmittalId=${featureId}`}
                  target="_blank"
                  data-testid="submittal-cards-manage-link"
                >
                  Manage
                </Link>
              ) : null
            }
            data-testid="submittal-cards-links-card"
          >
            <ErrorBoundary>
              <div
                className="h-[396px] overflow-auto"
                data-testid="submittal-cards-links-container"
              >
                <SubmittalDetailLinks
                  featureInstanceData={featureInstanceData}
                  actions={actions}
                  canEditLinkingPage={canEditLinkingPage}
                  isRevisionMode={isRevisionMode}
                  isVoidSubmittal={isVoidSubmittal}
                  isDeletedSubmittal={isDeletedSubmittal}
                  data-testid="submittal-cards-links"
                />
              </div>
            </ErrorBoundary>
          </EditableDetailsCardWrapper>
          {!isIntegrationMode && (
            <EditableDetailsCardWrapper
              title="Workflow History"
              data-testid="submittal-cards-workflow-history-card"
            >
              <ErrorBoundary>
                <div
                  className="h-[320px] overflow-auto"
                  data-testid="submittal-cards-workflow-history-container"
                >
                  <FeatureHistory
                    featureInstanceData={localFeatureInstanceData}
                    featureType={FeatureTypes.SUBMITTALS}
                    data-testid="submittal-cards-workflow-history"
                  />
                </div>
              </ErrorBoundary>
            </EditableDetailsCardWrapper>
          )}
        </div>
      </div>
    </div>
  );
}
export default SubmittalDetailsCards;
